import {useAufgabenHandling} from "../../hooks/aufgaben/useAufgabenHandling";
import {Box, capitalize, DialogActions, DialogContent, Typography} from "@mui/material";
import {aufgabenStyles} from "../../styles/aufgaben/aufgaben.styles";
import {Form, Formik, FormikProps} from "formik";
import {Aufgabe, Mandant} from "../../api";
import FieldsContentAufgabe from "../../components/aufgaben/FieldsContentAufgabe";
import SubmitButton from "../../components/SubmitButton";
import {useAufgabeUpdateForm} from "../../hooks/aufgaben/useAufgabeUpdateForm";
import {useAufgabe, useMandant} from "../../client/queries";
import {useParams} from "react-router";
import ErrorAlert from "../../components/ErrorAlert";
import {notPermitted} from "../../errors";
import MultipleGuard from "../../components/guards/MultipleGuard";
import {permissions} from "../../permissions";
import ButtonDeleteAufgabe from "../../components/aufgaben/ButtonDeleteAufgabe";
import ButtonShowTestCallDialog from "../../components/aufgaben/ButtonShowTestCallDialog";
import ButtonShowAiToAiDialog from "../../components/aufgaben/ButtonShowAiToAiDialog";
import type { AufgabeFormValues } from "../../types/aufgabenTypes";
import ButtonDuplicateAufgabe from "../../components/aufgaben/ButtonDuplicateAufgabe";
import SectionToolsAufgabe from "../../components/aufgaben/SectionToolsAufgabe";
import AufgabePromptVersionsSwitcher from "../../components/aufgaben/AufgabePromptVersionsSwitcher";

import {useUserMandant} from "../../hooks/useUserMandant";

export default function PageUpdateAufgabe() {
    const {aufgabeId = ""} = useParams<{
        aufgabeId: string;
    }>();
    const {translation, canEditAufgabeField} = useAufgabenHandling();
    const { isMandantAnrufer } = useUserMandant();
    const aufgabe: Aufgabe = useAufgabe(aufgabeId);
    const mandant: Mandant = useMandant(aufgabe.mandantId);
    const formikProps = useAufgabeUpdateForm(aufgabe);

    return (
        <MultipleGuard
            permissions={[
                permissions.aufgaben.edit,
                permissions.aufgaben.own_edit,
                permissions.aufgaben.show,
                permissions.aufgaben.own_show,
            ]}
            fallback={<ErrorAlert error={notPermitted()}/>}
        >

            <Formik {...formikProps}>
                {({isSubmitting, isValidating, isValid, dirty}: FormikProps<AufgabeFormValues>) => (
                    <Form id={'form-update-aufgabe'}>
                        <Box sx={aufgabenStyles.pageUpdateAufgabe.headerBoxWrapper}>
                            <Typography variant="h5">
                                {capitalize(translation('task-edit'))}
                            </Typography>
                            <MultipleGuard
                                permissions={[
                                    permissions.aufgaben.delete,
                                    permissions.aufgaben.own_delete,
                                ]}
                            >
                                <ButtonDeleteAufgabe aufgabeId={aufgabeId}/>
                            </MultipleGuard>
                        </Box>
                        <DialogContent id={'page-update-aufgabe-dialog-content'}>
                            <FieldsContentAufgabe canEditFields={canEditAufgabeField(mandant?.id ?? "")}
                                                  mandant={mandant}
                                                  aufgabeId={aufgabeId}
                                                  showPromptFields={false}
                                                  toolsSection={
                                                      <SectionToolsAufgabe aufgabe={aufgabe}
                                                                           canEdit={canEditAufgabeField(mandant?.id ?? "")}/>
                                                  }/>
                            <Box sx={{mt: 2}}>
                                <AufgabePromptVersionsSwitcher aufgabe={aufgabe} canEdit={canEditAufgabeField(aufgabe.mandantId)}/>
                            </Box>
                        </DialogContent>
                        <DialogActions id={'page-update-aufgabe-dialog-actions'}
                                       sx={aufgabenStyles.pageUpdateAufgabe.dialogActions}>
                            <ButtonDuplicateAufgabe aufgabeId={aufgabeId}/>
                            {!isMandantAnrufer && <ButtonShowTestCallDialog aufgabeId={aufgabeId} language={aufgabe.sprache}/>}
                            <ButtonShowAiToAiDialog aufgabeId={aufgabeId}/>
                            <SubmitButton id={'page-update-aufgabe-submit-button'}
                                          form={'form-update-aufgabe'}
                                          type={'submit'}
                                          variant={'contained'}
                                          color={'secondary'}
                                          loading={isSubmitting}
                                          disabled={isValidating
                                              || !isValid
                                              || !dirty
                                              || !canEditAufgabeField(aufgabe.mandantId)}
                            >
                                {isSubmitting ? capitalize(translation('updating')) : capitalize(translation('update'))}
                            </SubmitButton>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </MultipleGuard>
    );
}